import React, {useEffect, useMemo, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {Box, Button, List, ListItem, Divider, Text, Image} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getUrl} from '../../../utils/cloudinary-utils'
import {getKsBaseIcons, getZodiacSigns} from '../../../utils/engraving-utils'

const SpecialIcons = ({product, onIconSelect, displayIconType}) => {
    const [openedIcons, setOpenedIcons] = useState(displayIconType || 'ks')
    useEffect(() => {
        setOpenedIcons(displayIconType || 'ks')
    }, [displayIconType])
    const {staticIcons, selectedIcons} = useMemo(() => {
        const baseIcons = getKsBaseIcons()
        const dynamicIcons = [...baseIcons]

        if (product?.c_engravingLargeIcons?.includes('love')) {
            dynamicIcons.push({
                value: '\\ue017',
                label: 'love',
                code: 'E017',
                fontSize: '1.3rem'
            })
        }

        if (product?.c_engravingLargeIcons?.includes('xo')) {
            dynamicIcons.push({
                value: '\\ue018',
                label: 'xo',
                code: 'E018',
                fontSize: '0.7rem'
            })
        }

        const zodiacIcons = getZodiacSigns()

        const selectedIcons = []

        if (Array.isArray(product?.c_engravingLargeIcons)) {
            for (const iconMap of zodiacIcons) {
                const label = Object.keys(iconMap)[0]
                const value = iconMap[label]

                if (product.c_engravingLargeIcons.includes(label)) {
                    selectedIcons.push({
                        value,
                        label,
                        code: value.replace('\\u', '').toUpperCase()
                    })
                }
            }
        }

        return {
            staticIcons: dynamicIcons,
            selectedIcons
        }
    }, [product])

    const renderIcons = (icons, showLabel = false) =>
        icons.map((icon, index) => (
            <Box
                key={icon.value + index + showLabel}
                width={showLabel ? 'calc(50% - 8px)' : 'auto'}
                display={icon.hidden ? 'none' : 'flex'} // They will be displayed dynamically
                flexDirection={showLabel ? 'row' : 'column'}
                alignItems="center"
                gap="9px"
                textTransform="capitalize"
                justifyContent="flex-start"
            >
                <Button
                    className="btn-no-style special-item"
                    data-value={icon.value}
                    aria-label={icon.label}
                    variant="ghost"
                    width="45px"
                    height="45px"
                    borderRadius="0px"
                    backgroundColor="white"
                    onClick={() => {
                        onIconSelect(String.fromCharCode(parseInt(icon.code, 16)), icon.label)
                    }}
                >
                    <ListItem
                        as="span"
                        fontFamily="ksSansSerif"
                        color="black.100"
                        data-code={icon.code}
                        data-label={icon.label}
                        data-char-code={String.fromCharCode(parseInt(icon.code, 16))}
                        fontSize={icon.fontSize || '1.5625rem'}
                    >
                        {String.fromCharCode(parseInt(icon.code, 16))}
                    </ListItem>
                </Button>
                {showLabel && (
                    <Box fontSize="16px" color="black.100" fontWeight="300">
                        {icon.label}
                    </Box>
                )}
            </Box>
        ))

    return (
        <Box
            className="fonts-container"
            backgroundColor="gray.100"
            borderRadius="3px"
            border="1px solid"
            borderColor="purple.600"
        >
            <Box
                padding={openedIcons === 'ks' ? '15px' : '15px 15px 0px 15px'}
                display="inline-flex"
                justifyContent="space-between"
                width="100%"
                alignItems="center"
                cursor="pointer"
                onClick={() => {
                    setOpenedIcons(openedIcons === 'ks' ? '' : 'ks')
                }}
            >
                <Text
                    fontSize="23px"
                    fontWeight="400"
                    letterSpacing="0.115px"
                    fontFamily="heading"
                    color="black"
                >
                    <FormattedMessage id="ks.symbols" defaultMessage="Kendra Scott Symbols" />
                </Text>
                <Image
                    width="15px"
                    height="15px"
                    alt="arrow"
                    src={getUrl(
                        openedIcons === 'ks'
                            ? 'UIImages/images/minus-icon.svg'
                            : 'UIImages/images/plus-icon.svg'
                    )}
                />
            </Box>
            <List
                display="flex"
                flexWrap="wrap"
                gap="14px"
                margin="0px 15px 15px 15px"
                className={`ks-icons-list ${openedIcons === 'ks' ? 'show' : ''}`}
            >
                {renderIcons(staticIcons)}
            </List>

            {selectedIcons.length > 0 && (
                <>
                    <Divider
                        my="20px"
                        borderColor="gray.700"
                        margin={openedIcons === 'ks' ? '0px 15px 0px 15px' : '0 15px 0px 15px'}
                        width="auto"
                    />
                    <Box
                        padding="15px"
                        display="inline-flex"
                        justifyContent="space-between"
                        width="100%"
                        alignItems="center"
                        cursor="pointer"
                        onClick={() => {
                            setOpenedIcons(openedIcons === 'zodiac' ? '' : 'zodiac')
                        }}
                    >
                        <Text
                            fontSize="23px"
                            fontWeight="400"
                            letterSpacing="0.115px"
                            fontFamily="heading"
                            color="black"
                        >
                            <FormattedMessage id="zodiac.symbols" defaultMessage="Zodiac Signs" />
                        </Text>
                        <Image
                            width="15px"
                            height="15px"
                            alt="arrow"
                            src={getUrl(
                                openedIcons === 'zodiac'
                                    ? 'UIImages/images/minus-icon.svg'
                                    : 'UIImages/images/plus-icon.svg'
                            )}
                        />
                    </Box>
                    <List
                        display="flex"
                        flexWrap="wrap"
                        gap="14px"
                        margin={
                            openedIcons === 'zodiac' ? '0px 15px 20px 15px' : '0px 15px 0px 15px'
                        }
                        className={`zodiac-icons-list ${openedIcons === 'zodiac' ? 'show' : ''}`}
                    >
                        {renderIcons(selectedIcons, true)}
                    </List>
                </>
            )}
        </Box>
    )
}

SpecialIcons.propTypes = {
    product: PropTypes.shape({
        c_engravingLargeIcons: PropTypes.arrayOf(PropTypes.string)
    }),
    displayIconType: PropTypes.any,
    setSymbolsEngraved: PropTypes.any,
    symbolsEngraved: PropTypes.array,
    onIconSelect: PropTypes.func
}

export default SpecialIcons
